import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import {
  getMovieBySlug,
  getCategoryName,
  getMoviesByCategory,
  getHotMovies,
  allMovies,
  siteInfo,
} from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const movie = getMovieBySlug(id || '');
  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh]">
          <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4"></i>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">电影未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您查找的电影不存在或已被移除。</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all whitespace-nowrap text-sm"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedMovies = getMoviesByCategory(movie.category)
    .filter((m) => m.id !== movie.id)
    .slice(0, 5);

  const hotMovies = getHotMovies().filter((m) => m.id !== movie.id).slice(0, 5);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    url: `https://555mov.com/article/${movie.slug}`,
    image: movie.poster,
    dateCreated: movie.releaseDate,
    description: movie.description,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
    },
    duration: movie.duration,
  };

  return (
    <>
      <PageSEO
        title={`${movie.title} - ${siteInfo.fullName}`}
        description={`${movie.description} ${movie.title}，${movie.year}年${getCategoryName(movie.category)}，导演${movie.director}，主演${movie.actors.join('、')}。555影视免费在线观看。`}
        keywords={`${movie.title},${getCategoryName(movie.category)},${movie.director},${movie.actors.slice(0, 3).join(',')},555影视,免费电影,在线观看`}
        canonicalUrl={`https://555mov.com/article/${movie.slug}`}
        ogImage={movie.poster}
        ogType="article"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: getCategoryName(movie.category), href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>

          {/* Article Content */}
          <article className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0">
                <div className="w-full sm:w-64 lg:w-72 aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                    title={`${movie.title}电影海报 - 555影视`}
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="inline-flex items-center gap-1 bg-accent-100 text-accent-900 px-3 py-1 rounded-full text-sm font-medium">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    {movie.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="text-sm text-foreground-500">{movie.language}</span>
                  <span className="inline-block bg-background-100 text-foreground-600 px-3 py-1 rounded-full text-xs">
                    {getCategoryName(movie.category)}
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap min-w-[3rem]">导演：</span>
                    <span className="text-sm text-foreground-600">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap min-w-[3rem]">主演：</span>
                    <span className="text-sm text-foreground-600">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap min-w-[3rem]">地区：</span>
                    <span className="text-sm text-foreground-600">{movie.region}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap min-w-[3rem]">上映：</span>
                    <span className="text-sm text-foreground-600">{movie.releaseDate}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.summary}</p>
                </div>

                <div>
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">精彩看点</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>
              </div>
            </div>
          </article>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950">
                  同类电影推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} {...m} />
                ))}
              </div>
            </section>
          )}

          {/* Hot Movies */}
          {hotMovies.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 pb-14">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950">
                  热门推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {hotMovies.map((m) => (
                  <MovieCard key={m.id} {...m} />
                ))}
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}